/**
 * Turns a better-auth failure into something a person can act on.
 *
 * "Sign in failed" is what the API says when the password is wrong *and* what
 * the client says when the backend is not running — and those need opposite
 * responses from the reader. Distinguishing them costs one branch and saves the
 * "I can't log in" support round-trip.
 */
export function authErrorMessage(
  result: { error?: { code?: string; message?: string; status?: number } | null } | null,
  fallback: string,
): string {
  const error = result?.error;
  if (!error) return fallback;

  // better-auth surfaces an unreachable server as status 0 / no code: the
  // request never got an HTTP answer at all.
  if (!error.status || error.status === 0 || error.status >= 500) {
    return "Can't reach the server. Is the backend running on port 8787?";
  }

  switch (error.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return 'That email and password do not match.';
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'An account with that email already exists. Try signing in instead.';
    case 'INVALID_ORIGIN':
      return `This page is served from ${window.location.origin}, which the server does not trust. Open it at http://localhost:5273.`;
    case 'PASSWORD_TOO_SHORT':
      return 'Password must be at least 8 characters.';
    default:
      return error.message || fallback;
  }
}

/** A thrown error (network-level) never reached the server at all. */
export function networkErrorMessage(): string {
  return "Can't reach the server. Is the backend running on port 8787?";
}

/**
 * Turns the `?error=` a social sign-in redirects back with into something a
 * person can act on. better-auth puts a code or a message there depending on
 * where the flow died, so match loosely and always return something.
 */
export function socialErrorMessage(raw: string | null): string | null {
  if (!raw) return null;
  const value = raw.toLowerCase();

  if (value.includes('signup_not_allowed') || value.includes('forbidden')) {
    return 'This deployment is invite-only. Ask Stephen to add your Google address.';
  }
  if (value.includes('access_denied') || value.includes('cancel')) {
    return 'You cancelled the Google sign-in.';
  }
  return 'Google sign-in did not complete. Try again, or use your email and password.';
}
